import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  ShoppingCart, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
  Mail, Globe, Hash, ChevronLeft, FileText, Webhook, ShieldCheck, ShieldAlert
} from 'lucide-react';
import { apiFetch } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  ok: { label: 'Erfolgreich', icon: CheckCircle, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  processing: { label: 'In Bearbeitung', icon: Clock, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  invalid_signature: { label: 'Signatur ungültig', icon: AlertTriangle, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  error: { label: 'Fehler', icon: XCircle, color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  connection_test_ok: { label: 'Test', icon: CheckCircle, color: 'bg-gray-500/20 text-gray-300 border-gray-500/30' } };

const EVENT_LABEL = {
  order_complete: 'Bestellung',
  rebill: 'Folgezahlung',
  refund: 'Erstattung',
  chargeback: 'Rückbuchung',
  cancel: 'Kündigung',
  connection_test: 'Test' };

const formatDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <Card className="bg-gray-900/50 border-gray-800" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
        {Icon && <Icon className={`h-7 w-7 ${color} opacity-60`} />}
      </div>
    </CardContent>
  </Card>
);

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, role, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, ok: 0, error: 0, invalid_signature: 0, processing: 0, tests: 0 });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // event filter
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API}/api/admin/orders`);
      url.searchParams.set('limit', '50');
      if (filter) url.searchParams.set('event', filter);
      // IPN-Health parallel laden — separater Endpoint, unabhängiger Fehlerpfad
      const [ordersRes, healthRes] = await Promise.all([
        apiFetch(url, { headers: {} }),
        apiFetch(`${API}/api/admin/ipn-health`, { headers: {} }).catch(() => null),
      ]);
      if (healthRes && healthRes.ok) {
        setHealth(await healthRes.json());
      }
      const res = ordersRes;
      if (res.status === 403) {
        setError('Nur für Admin-Konten zugänglich.');
        setOrders([]);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setOrders(data.orders || []);
        setStats(data.stats || stats);
      } else {
        setError(data.detail || 'Konnte Bestellungen nicht laden.');
      }
    } catch (e) {
      setError('Netzwerkfehler beim Laden der Bestellungen.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!(isAdmin || role === 'admin')) {
      setError('Nur für Admin-Konten zugänglich.');
      setLoading(false);
      return;
    }
    load();
  }, [authLoading, isAuthenticated, isAdmin, role, load, navigate]);

  if (authLoading || (loading && orders.length === 0 && !error)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Helmet>
        <title>Bestellungen-Monitor – SmartVolt Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="bg-black/50 border-b border-yellow-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-yellow-500" /> Bestellungen-Monitor
              </h1>
              <p className="text-xs text-gray-400">Letzte 50 Digistore24-IPN-Events</p>
            </div>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-yellow-500 text-black hover:bg-yellow-400 font-medium"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {error ? (
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="py-6 text-center">
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-red-300 font-medium">{error}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* IPN-Endpoint-Health */}
            {health && (
              <Card
                className={`mb-4 border ${
                  health.status_color === 'green' ? 'bg-emerald-500/5 border-emerald-500/30' :
                  health.status_color === 'amber' ? 'bg-amber-500/5 border-amber-500/40' :
                  health.status_color === 'red' ? 'bg-rose-500/5 border-rose-500/40' :
                  'bg-gray-800/40 border-gray-700'
                }`}
                data-testid="ipn-health-card"
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        health.status_color === 'green' ? 'bg-emerald-500/15' :
                        health.status_color === 'amber' ? 'bg-amber-500/15' :
                        health.status_color === 'red' ? 'bg-rose-500/15' : 'bg-gray-700/40'
                      }`}>
                        <Webhook className={`h-5 w-5 ${
                          health.status_color === 'green' ? 'text-emerald-300' :
                          health.status_color === 'amber' ? 'text-amber-300' :
                          health.status_color === 'red' ? 'text-rose-300' : 'text-gray-300'
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-semibold">Digistore24-Webhook</p>
                          <Badge className={
                            health.status_color === 'green' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                            health.status_color === 'amber' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                            health.status_color === 'red' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' :
                            'bg-gray-700/40 text-gray-300 border-gray-600'
                          }>
                            {health.status_color === 'green' ? 'aktiv' : health.status_color === 'amber' ? 'unvollständig' : health.status_color === 'red' ? 'Fehler' : 'wartend'}
                          </Badge>
                          {health.passphrase_configured ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                              <ShieldCheck className="h-3.5 w-3.5" /> Passphrase gesetzt
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                              <ShieldAlert className="h-3.5 w-3.5" /> Passphrase fehlt
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-300 mt-1">{health.status_message}</p>
                        <code className="text-xs text-gray-500 mt-1 block break-all">{health.endpoint_url}</code>
                        {health.last_event_at && (
                          <p className="text-xs text-gray-400 mt-1.5">
                            Letzter Event: <span className="text-white">{EVENT_LABEL[health.last_event_type] || health.last_event_type}</span> · {formatDate(health.last_event_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">30 Tage</p>
                      <p className="text-2xl font-bold text-white">{health.total_30d}</p>
                      <p className="text-xs text-gray-500">IPN-Events</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status-Kacheln */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <StatCard label="Gesamt" value={stats.total} color="text-white" icon={FileText} />
              <StatCard label="Erfolg" value={stats.ok} color="text-green-400" icon={CheckCircle} />
              <StatCard label="In Arbeit" value={stats.processing} color="text-blue-400" icon={Clock} />
              <StatCard label="Sig. ungültig" value={stats.invalid_signature} color="text-yellow-400" icon={AlertTriangle} />
              <StatCard label="Fehler" value={stats.error} color="text-red-400" icon={XCircle} />
            </div>

            {/* Filter */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="event-filters">
              {[
                { id: '', label: 'Alle' },
                { id: 'order_complete', label: 'Bestellungen' },
                { id: 'rebill', label: 'Folgezahlungen' },
                { id: 'refund', label: 'Erstattungen' },
                { id: 'cancel', label: 'Kündigungen' },
                { id: 'connection_test', label: 'Tests' },
              ].map((f) => (
                <button
                  key={f.id || 'all'}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    filter === f.id
                      ? 'bg-yellow-500 text-black border-yellow-500'
                      : 'bg-gray-900/50 text-gray-300 border-gray-800 hover:border-yellow-500/50'
                  }`}
                  data-testid={`filter-${f.id || 'all'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Orders-Tabelle */}
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-base">Bestellungen ({orders.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {orders.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>Noch keine Bestellungen.</p>
                    <p className="text-xs mt-1">Sobald Digistore24 IPN-Events sendet, erscheinen sie hier.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="orders-table">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-800">
                          <th className="px-4 py-3">Datum</th>
                          <th className="px-4 py-3">Event</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Kunde</th>
                          <th className="px-4 py-3">Bestell-ID</th>
                          <th className="px-4 py-3 text-right">Lizenzen</th>
                          <th className="px-4 py-3 text-right">Betrag</th>
                          <th className="px-4 py-3">Land</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o, i) => {
                          const meta = STATUS_META[o.status] || STATUS_META.processing;
                          const StatusIcon = meta.icon;
                          const fullName = [o.buyer_first_name, o.buyer_last_name].filter(Boolean).join(' ');
                          return (
                            <tr
                              key={`${o.order_id || 'noid'}-${i}`}
                              className="border-b border-gray-800/50 hover:bg-black/30 transition-colors"
                              data-testid={`order-row-${i}`}
                            >
                              <td className="px-4 py-3 text-gray-300 whitespace-nowrap text-xs">
                                {formatDate(o.processed_at)}
                              </td>
                              <td className="px-4 py-3">
                                <Badge className="bg-gray-800/60 text-gray-200 border border-gray-700 text-[10px]">
                                  {EVENT_LABEL[o.event] || o.event || '–'}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <Badge className={`${meta.color} border text-[10px] inline-flex items-center gap-1`}>
                                  <StatusIcon className="w-3 h-3" /> {meta.label}
                                </Badge>
                                {o.status === 'error' && o.error && (
                                  <div className="text-[10px] text-red-400/80 mt-1 max-w-[180px] truncate" title={o.error}>
                                    {o.error}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-white font-medium leading-tight">
                                  {fullName || '–'}
                                </div>
                                {o.email && (
                                  <div className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                                    <Mail className="w-3 h-3" /> {o.email}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <code className="text-xs text-gray-400 bg-black/40 px-2 py-0.5 rounded">
                                  {o.order_id || '–'}
                                </code>
                                {o.product_id && (
                                  <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                                    <Hash className="w-2.5 h-2.5" /> {o.product_id}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right text-white font-semibold">
                                {o.quantity || '–'}
                              </td>
                              <td className="px-4 py-3 text-right text-yellow-400 font-semibold whitespace-nowrap">
                                {o.amount ? `${o.amount} ${o.currency || ''}`.trim() : '–'}
                              </td>
                              <td className="px-4 py-3 text-gray-300">
                                {o.buyer_country ? (
                                  <span className="inline-flex items-center gap-1 text-xs">
                                    <Globe className="w-3 h-3 text-gray-500" /> {o.buyer_country}
                                  </span>
                                ) : '–'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-gray-600 mt-4 text-center">
              Live-Daten aus <code className="bg-black/40 px-1 rounded">digistore_ipn_logs</code> – sortiert nach Eingangszeit
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminOrdersPage;
